import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { cn } from '../../utils/cn.js';

interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

interface FilterPillsProps {
  options: readonly FilterOption[];
  value: string;
  onChange: (id: string) => void;
  /** Names the filter for screen readers, e.g. "Status". */
  'aria-label'?: string;
  className?: string;
}

/**
 * A one-of-N quick filter above a list (segmented control on react-aria `ToggleButtonGroup`: arrow keys move, one
 * option is always selected). More than ~5 options → use a `Select` instead.
 */
export function FilterPills({ options, value, onChange, className, ...aria }: FilterPillsProps) {
  return (
    <ToggleButtonGroup
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const [next] = keys;
        if (next !== undefined) onChange(String(next));
      }}
      aria-label={aria['aria-label'] ?? 'Filter'}
      className={cn('inline-flex flex-wrap gap-1', className)}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.id}
          id={option.id}
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-text-100 outline-none transition-colors',
            'hover:text-title-50 data-focus-visible:ring-2 data-focus-visible:ring-primary-500',
            'data-selected:bg-tab-secondary-active-background data-selected:text-neutral-brand-color',
          )}
        >
          {option.label}
          {option.count !== undefined && <span className="text-xs text-muted tabular-nums">{option.count}</span>}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
